import React, { useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  Toolbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Refresh,
  Print,
  Email,
  History,
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { invoiceApi } from '../services/api';
import {
  InvoicePaymentRequest,
  InvoiceRecord,
  isInvoicePayableStatus,
  normalizeInvoiceStatus,
} from '../types/invoice';
import { Reservation } from '../types/reservation';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ReservationStatusChip } from '../components/ReservationStatusChip';
import { DateInputField } from '../components/DateInputField';
import { formatDate, formatCurrency } from '../utils/formatters';
import { safeFilePart } from '../utils/downloadFile';
import { EmailSendDialog } from '../components/email/EmailSendDialog';
import { EmailLogPanel } from '../components/email/EmailLogPanel';
import { InvoiceDetailsPanel } from '../components/invoices/InvoiceDetailsPanel';
import { useTranslation } from 'react-i18next';
import { TableFilterCell } from '../components/TableFilterCell';
import { enumLabel, statusLabel } from '../i18n/translate';
import { getInvoiceBillToInfo, getReservationBillToInfo } from '../utils/billing';

type ViewMode = 'invoices' | 'uninvoiced';
type InvoicePaymentMethod = 'CASH' | 'CARD' | 'BANK_TRANSFER';
type InvoicePaymentForm = {
  amount: string;
  paymentMethod: InvoicePaymentMethod;
  paymentReference: string;
  notes: string;
  paymentDate: string;
};

const todayInput = () => dayjs().format('YYYY-MM-DD');

const getRemainingInvoiceBalance = (invoice?: InvoiceRecord | null): number => {
  if (!invoice) {
    return 0;
  }

  const explicitBalance = Number(invoice.balanceDue);
  if (Number.isFinite(explicitBalance)) {
    return Math.max(explicitBalance, 0);
  }

  const derivedBalance = Number(invoice.totalAmount || 0) - Number(invoice.amountPaid || 0);
  return Number.isFinite(derivedBalance) ? Math.max(derivedBalance, 0) : 0;
};

const normalizeInvoicePaymentMethod = (value?: string | null): InvoicePaymentMethod => {
  if (value === 'CARD' || value === 'BANK_TRANSFER' || value === 'CASH') {
    return value;
  }

  return 'CASH';
};

const buildInvoicePaymentForm = (invoice?: InvoiceRecord | null): InvoicePaymentForm => ({
  amount: invoice ? getRemainingInvoiceBalance(invoice).toFixed(2) : '',
  paymentMethod: normalizeInvoicePaymentMethod(invoice?.paymentMethod),
  paymentReference: invoice?.paymentReference || '',
  notes: '',
  paymentDate: todayInput(),
});

export const InvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRecord | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('invoices');
  const [selectedReservations, setSelectedReservations] = useState<Set<number>>(new Set());
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState<InvoicePaymentForm>(buildInvoicePaymentForm());
  
  // Column filters and sorting for Invoices view
  const [invoiceColumnFilters, setInvoiceColumnFilters] = useState({
    invoiceNumber: '',
    date: '',
    reservation: '',
    billTo: '',
    amount: '',
    status: '',
    dueDate: '',
  });
  
  // Column filters and sorting for Uninvoiced view
  const [uninvoicedColumnFilters, setUninvoicedColumnFilters] = useState({
    reservationNumber: '',
    date: '',
    billTo: '',
    vehicle: '',
    amount: '',
    status: '',
  });
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Fetch invoices
  const { data: invoices = [], isLoading: loadingInvoices } = useQuery(
    ['invoices', companyId],
    () => invoiceApi.getAll(),
    { enabled: !!companyId && viewMode === 'invoices' }
  );

  // Fetch uninvoiced reservations
  const { data: uninvoicedReservations = [], isLoading: loadingReservations } = useQuery(
    ['uninvoicedReservations', companyId],
    () => invoiceApi.getUninvoicedReservations(),
    { enabled: !!companyId && viewMode === 'uninvoiced' }
  );

  // Create invoice mutation
  const createInvoiceMutation = useMutation(
    (reservationIds: number[]) => invoiceApi.createFromReservations(reservationIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices');
        queryClient.invalidateQueries('uninvoicedReservations');
        toast.success(t('invoices.messages.created'));
        setSelectedReservations(new Set());
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('invoices.messages.createFailed'));
      },
    }
  );

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation(
    (id: string) => invoiceApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices');
        toast.success(t('invoices.messages.deleted'));
        setSelectedInvoice(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('invoices.messages.deleteFailed'));
      },
    }
  );

  // Record invoice payment mutation
  const recordPaymentMutation = useMutation(
    ({ id, paymentData }: { id: string; paymentData: InvoicePaymentRequest }) =>
      invoiceApi.recordPayment(id, paymentData),
    {
      onSuccess: async (updatedInvoice) => {
        queryClient.setQueryData<InvoiceRecord[] | undefined>(
          ['invoices', companyId],
          (currentInvoices) =>
            Array.isArray(currentInvoices)
              ? currentInvoices.map((invoice) =>
                  invoice.id === updatedInvoice.id ? updatedInvoice : invoice
                )
              : currentInvoices
        );
        setSelectedInvoice(updatedInvoice);
        setPaymentDialogOpen(false);
        setPaymentForm(buildInvoicePaymentForm(updatedInvoice));
        toast.success(
          updatedInvoice.status === 'PAID'
            ? t('invoices.messages.paid')
            : t('invoices.messages.paymentRecorded', 'Payment recorded successfully')
        );
        await queryClient.invalidateQueries(['invoices', companyId]);
        await queryClient.invalidateQueries(['availablePaymentInvoices', companyId]);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('invoices.messages.paymentFailed', 'Failed to record invoice payment')
        );
      },
    }
  );

  const handleColumnFilterChange = (column: string, value: string) => {
    if (viewMode === 'invoices') {
      setInvoiceColumnFilters(prev => ({ ...prev, [column]: value }));
    } else {
      setUninvoicedColumnFilters(prev => ({ ...prev, [column]: value }));
    }
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else {
          return null;
        }
      } else {
        return { key, direction: 'asc' };
      }
    });
  };

  const renderFilterCell = (
    placeholder: string,
    value: string,
    onChange: (value: string) => void,
    sortKey: string,
    width: string,
  ) => (
    <TableFilterCell
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onSort={() => handleSort(sortKey)}
      sortDirection={sortConfig?.key === sortKey ? sortConfig.direction : null}
      width={width}
      cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
    />
  );

  // Filter and sort data
  let filteredData: any[] = [];
  
  if (viewMode === 'invoices') {
    filteredData = invoices.filter((invoice: InvoiceRecord) => {
      const invoiceDateStr = invoice.invoiceDate ? formatDate(invoice.invoiceDate).toLowerCase() : '';
      const dueDateStr = invoice.dueDate ? formatDate(invoice.dueDate).toLowerCase() : '';
      const billTo = getInvoiceBillToInfo(invoice);
      const reservationNum = invoice.reservation?.reservationNumber || '';
      
      return (
        (!invoiceColumnFilters.invoiceNumber || (invoice.invoiceNumber || '').toLowerCase().includes(invoiceColumnFilters.invoiceNumber.toLowerCase())) &&
        (!invoiceColumnFilters.date || invoiceDateStr.includes(invoiceColumnFilters.date.toLowerCase())) &&
        (!invoiceColumnFilters.reservation || reservationNum.toLowerCase().includes(invoiceColumnFilters.reservation.toLowerCase())) &&
        (!invoiceColumnFilters.billTo || billTo.displayName.toLowerCase().includes(invoiceColumnFilters.billTo.toLowerCase())) &&
        (!invoiceColumnFilters.amount || (invoice.totalAmount?.toString() || '').includes(invoiceColumnFilters.amount)) &&
        (!invoiceColumnFilters.status || (invoice.status || '').toLowerCase().includes(invoiceColumnFilters.status.toLowerCase())) &&
        (!invoiceColumnFilters.dueDate || dueDateStr.includes(invoiceColumnFilters.dueDate.toLowerCase()))
      );
    });

    // Sort invoices
    if (sortConfig) {
      filteredData = [...filteredData].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'invoiceNumber':
            aValue = (a.invoiceNumber || '').toLowerCase();
            bValue = (b.invoiceNumber || '').toLowerCase();
            break;
          case 'date':
            aValue = a.invoiceDate || '';
            bValue = b.invoiceDate || '';
            break;
          case 'reservation':
            aValue = (a.reservation?.reservationNumber || '').toLowerCase();
            bValue = (b.reservation?.reservationNumber || '').toLowerCase();
            break;
          case 'billTo':
            aValue = getInvoiceBillToInfo(a).displayName.toLowerCase();
            bValue = getInvoiceBillToInfo(b).displayName.toLowerCase();
            break;
          case 'amount':
            aValue = parseFloat(a.totalAmount?.toString() || '0') || 0;
            bValue = parseFloat(b.totalAmount?.toString() || '0') || 0;
            break;
          case 'status':
            aValue = (a.status || '').toLowerCase();
            bValue = (b.status || '').toLowerCase();
            break;
          case 'dueDate':
            aValue = a.dueDate || '';
            bValue = b.dueDate || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
  } else {
    filteredData = uninvoicedReservations.filter((reservation: Reservation) => {
      const dateStr = reservation.startDate
        ? `${formatDate(reservation.startDate)} - ${reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}`.toLowerCase()
        : '';
      const billTo = getReservationBillToInfo(reservation);
      const vehicleStr = reservation.vehicle ? `${reservation.vehicle.make} ${reservation.vehicle.model}`.toLowerCase() : '';
      
      return (
        (!uninvoicedColumnFilters.reservationNumber || (reservation.reservationNumber || '').toLowerCase().includes(uninvoicedColumnFilters.reservationNumber.toLowerCase())) &&
        (!uninvoicedColumnFilters.date || dateStr.includes(uninvoicedColumnFilters.date.toLowerCase())) &&
        (!uninvoicedColumnFilters.billTo || billTo.displayName.toLowerCase().includes(uninvoicedColumnFilters.billTo.toLowerCase())) &&
        (!uninvoicedColumnFilters.vehicle || vehicleStr.includes(uninvoicedColumnFilters.vehicle.toLowerCase())) &&
        (!uninvoicedColumnFilters.amount || (reservation.totalAmount?.toString() || '').includes(uninvoicedColumnFilters.amount)) &&
        (!uninvoicedColumnFilters.status || (reservation.status || '').toLowerCase().includes(uninvoicedColumnFilters.status.toLowerCase()))
      );
    });

    // Sort uninvoiced reservations
    if (sortConfig) {
      filteredData = [...filteredData].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'reservationNumber':
            aValue = (a.reservationNumber || '').toLowerCase();
            bValue = (b.reservationNumber || '').toLowerCase();
            break;
          case 'date':
            aValue = a.startDate || '';
            bValue = b.startDate || '';
            break;
          case 'billTo':
            aValue = getReservationBillToInfo(a).displayName.toLowerCase();
            bValue = getReservationBillToInfo(b).displayName.toLowerCase();
            break;
          case 'vehicle':
            aValue = a.vehicle ? `${a.vehicle.make} ${a.vehicle.model}`.toLowerCase() : '';
            bValue = b.vehicle ? `${b.vehicle.make} ${b.vehicle.model}`.toLowerCase() : '';
            break;
          case 'amount':
            aValue = parseFloat(a.totalAmount?.toString() || '0') || 0;
            bValue = parseFloat(b.totalAmount?.toString() || '0') || 0;
            break;
          case 'status':
            aValue = (a.status || '').toLowerCase();
            bValue = (b.status || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
  }

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const paginatedReservationIds =
    viewMode === 'uninvoiced'
      ? paginatedData.map((reservation: Reservation) => reservation.id)
      : [];
  const allVisibleReservationsSelected =
    paginatedReservationIds.length > 0 &&
    paginatedReservationIds.every((reservationId: number) => selectedReservations.has(reservationId));
  const someVisibleReservationsSelected =
    paginatedReservationIds.some((reservationId: number) => selectedReservations.has(reservationId));
  const selectedReservationRecords = uninvoicedReservations.filter((reservation) =>
    selectedReservations.has(reservation.id)
  );
  const selectedBillToKey = selectedReservationRecords[0]
    ? getReservationBillToInfo(selectedReservationRecords[0]).key
    : null;

  // Handlers

  const handleRowClick = (item: any) => {
    if (viewMode === 'invoices') {
      setSelectedInvoice(item);
    }
  };

  const handleNewInvoice = () => {
    if (viewMode !== 'uninvoiced') {
      toast.error(t('invoices.validation.switchToUninvoiced'));
      return;
    }
    handleCreateInvoice();
  };

  const handleToggleReservation = (reservationId: number) => {
    const reservation = uninvoicedReservations.find((item) => item.id === reservationId);
    if (!reservation) {
      return;
    }

    const billTo = getReservationBillToInfo(reservation);
    if (!selectedReservations.has(reservationId) && selectedBillToKey && selectedBillToKey !== billTo.key) {
      toast.error(
        t('invoices.validation.sameBillTo', 'Selected reservations must belong to the same billing recipient.')
      );
      return;
    }

    const newSelected = new Set(selectedReservations);
    if (newSelected.has(reservationId)) {
      newSelected.delete(reservationId);
    } else {
      newSelected.add(reservationId);
    }
    setSelectedReservations(newSelected);
  };

  const handleToggleVisibleReservations = () => {
    const newSelected = new Set(selectedReservations);
    const visibleReservations = paginatedData as Reservation[];

    if (allVisibleReservationsSelected) {
      paginatedReservationIds.forEach((reservationId: number) => newSelected.delete(reservationId));
    } else {
      visibleReservations.forEach((reservation) => {
        const billTo = getReservationBillToInfo(reservation);
        if (!selectedBillToKey || selectedBillToKey === billTo.key) {
          newSelected.add(reservation.id);
        }
      });
    }

    setSelectedReservations(newSelected);
  };

  const handleCreateInvoice = () => {
    if (selectedReservations.size === 0) {
      toast.error(t('invoices.validation.selectReservation'));
      return;
    }
    createInvoiceMutation.mutate(Array.from(selectedReservations));
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDeleteInvoice = () => {
    if (selectedInvoice) {
      setDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDeleteInvoice = () => {
    if (selectedInvoice) {
      deleteInvoiceMutation.mutate(selectedInvoice.id.toString());
    }
    setDeleteConfirmOpen(false);
  };

  const handleOpenPaymentDialog = () => {
    if (!selectedInvoice) {
      toast.error(t('invoices.validation.selectInvoice'));
      return;
    }

    setPaymentForm(buildInvoicePaymentForm(selectedInvoice));
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = () => {
    if (!selectedInvoice) {
      toast.error(t('invoices.validation.selectInvoice'));
      return;
    }

    const amount = Number(paymentForm.amount);
    const remainingBalance = getRemainingInvoiceBalance(selectedInvoice);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(
        t('invoices.validation.validPaymentAmount', 'Enter a valid payment amount greater than zero')
      );
      return;
    }

    if (amount > remainingBalance) {
      toast.error(
        t(
          'invoices.validation.paymentAmountExceedsBalance',
          'Payment amount cannot be greater than the remaining balance'
        )
      );
      return;
    }

    recordPaymentMutation.mutate({
      id: selectedInvoice.id.toString(),
      paymentData: {
        amount,
        paymentMethod: paymentForm.paymentMethod,
        paymentReference: paymentForm.paymentReference.trim() || undefined,
        notes: paymentForm.notes.trim() || undefined,
        paymentDate: paymentForm.paymentDate || undefined,
      },
    });
  };

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLogDialogOpen, setEmailLogDialogOpen] = useState(false);

  const handleOpenEmailDialog = () => {
    if (!selectedInvoice) {
      toast.error(t('invoices.validation.selectInvoice'));
      return;
    }
    setEmailDialogOpen(true);
  };

  const handlePrintInvoicePdf = async () => {
    if (!selectedInvoice) {
      toast.error(t('invoices.validation.selectInvoice'));
      return;
    }

    try {
      const blob = await invoiceApi.downloadPdf(selectedInvoice.id.toString());
      const fileName = `invoice-${safeFilePart(selectedInvoice.invoiceNumber, selectedInvoice.id.toString())}.pdf`;
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(t('invoices.messages.pdfDownloaded'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('invoices.messages.downloadFailed'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (normalizeInvoiceStatus(status)) {
      case 'PAID':
        return 'success';
      case 'UNPAID':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      case 'SENT':
        return 'info';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  const selectedInvoiceCanBeDeleted =
    viewMode === 'invoices' && normalizeInvoiceStatus(selectedInvoice?.status) === 'UNPAID';
  const selectedInvoiceCanRecordPayment =
    viewMode === 'invoices' &&
    !!selectedInvoice &&
    !selectedInvoice.linkedToPayment &&
    isInvoicePayableStatus(selectedInvoice.status) &&
    getRemainingInvoiceBalance(selectedInvoice) > 0;
  const selectedInvoiceRemainingBalance = getRemainingInvoiceBalance(selectedInvoice);

  return (
    <Box>
      {/* Sticky Toolbar with New, Save, Delete buttons - Connected to top of page */}
      <Box
        sx={{ 
          position: 'sticky',
          top: 64, // Position below AppBar (AppBar height is typically 64px)
          zIndex: 1100, // Higher than AppBar (which is 1100) to stay on top
          bgcolor: 'background.paper', 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%'
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar 
            sx={{ 
              pl: { xs: 2, sm: 3 }, 
              pr: { xs: 2, sm: 3 }, 
              minHeight: '48px !important', 
              py: 0.5
            }}
          >
            {/* Action Buttons - New, Delete */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {viewMode === 'uninvoiced' && (
                <Button
                  variant="contained"
                  startIcon={createInvoiceMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  onClick={handleNewInvoice}
                  disabled={selectedReservations.size === 0 || createInvoiceMutation.isLoading}
                  color="primary"
                  size="small"
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    py: 0.75,
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  {selectedReservations.size > 0 ? t('invoices.newSelected', { count: selectedReservations.size }) : t('common.new')}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={
                  recordPaymentMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={handleOpenPaymentDialog}
                disabled={!selectedInvoiceCanRecordPayment || recordPaymentMutation.isLoading}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('invoices.recordPayment', 'Record Payment')}</Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteInvoice}
                disabled={!selectedInvoiceCanBeDeleted}
                color="error"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.delete')}</Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrintInvoicePdf}
                disabled={!selectedInvoice || viewMode !== 'invoices'}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.print')}</Button>
              <Button
                variant="contained"
                startIcon={<Email />}
                onClick={handleOpenEmailDialog}
                disabled={!selectedInvoice || viewMode !== 'invoices'}
                color="primary"
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.email', 'Email')}</Button>
              <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => setEmailLogDialogOpen(true)}
                disabled={!selectedInvoice || viewMode !== 'invoices'}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('invoices.sentEmails', 'Sent Emails')}</Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              {/* Secondary Toolbar - Refresh, View Mode, Pagination, Clear Filters */}
              <Toolbar sx={{ 
                pl: { xs: 2, sm: 3 }, 
                pr: { xs: 2, sm: 3 }, 
                minHeight: '48px !important', 
                py: 0.5, 
                bgcolor: 'background.paper', 
                borderBottom: '1px solid', 
                borderColor: 'divider' 
              }}>
                {/* Secondary Action Buttons - Left */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    onClick={() => {
                      queryClient.invalidateQueries('invoices');
                      queryClient.invalidateQueries('uninvoicedReservations');
                    }}
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5
                      }
                    }}
                  >{t('common.refresh')}</Button>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '24px' }} />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('invoices.viewMode')}</InputLabel>
                    <Select
                      value={viewMode}
                      label={t('invoices.viewMode')}
                      onChange={(e) => {
                        setViewMode(e.target.value as ViewMode);
                        setPage(0);
                        setSelectedInvoice(null);
                        setSelectedReservations(new Set());
                        setSortConfig(null);
                      }}
                      sx={{ height: '28px', fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="invoices">{t('invoices.generated')}</MenuItem>
                      <MenuItem value="uninvoiced">{t('invoices.uninvoiced')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Pagination Controls - Center */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '80px', textAlign: 'center' }}>
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredData.length)} {t('common.of')} {filteredData.length}
                </Typography>
                
                {/* Page Numbers */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {Array.from({ length: Math.min(5, Math.ceil(filteredData.length / rowsPerPage)) }, (_, i) => {
                    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 2) {
                      pageNum = i;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    if (pageNum < 0 || pageNum >= totalPages) return null;

                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: '32px',
                          height: '28px',
                          px: 1,
                          fontSize: '0.8125rem',
                          bgcolor: page === pageNum ? 'primary.main' : 'transparent',
                          color: page === pageNum ? 'white' : 'text.primary',
                          '&:hover': {
                            bgcolor: page === pageNum ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                  {Math.ceil(filteredData.length / rowsPerPage) > 5 && page < Math.ceil(filteredData.length / rowsPerPage) - 3 && (
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                      ...
                    </Typography>
                  )}
                  {Math.ceil(filteredData.length / rowsPerPage) > 5 && page < Math.ceil(filteredData.length / rowsPerPage) - 3 && (
                    <Button
                      size="small"
                      onClick={() => setPage(Math.ceil(filteredData.length / rowsPerPage) - 1)}
                      sx={{
                        minWidth: '32px',
                        height: '28px',
                        px: 1,
                        fontSize: '0.8125rem',
                      }}
                    >
                      {Math.ceil(filteredData.length / rowsPerPage)}
                    </Button>
                  )}
                </Box>

                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.min(Math.ceil(filteredData.length / rowsPerPage) - 1, prev + 1))}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  size="small"
                  sx={{ 
                    height: '28px',
                    fontSize: '0.8125rem',
                    ml: 1,
                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                  }}
                  renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Clear Filters Button - Right */}
              <Button
                size="small"
                startIcon={<FilterListOff fontSize="small" />}
                onClick={() => {
                  if (viewMode === 'invoices') {
                    setInvoiceColumnFilters({
                      invoiceNumber: '',
                      date: '',
                      reservation: '',
                      billTo: '',
                      amount: '',
                      status: '',
                      dueDate: '',
                    });
                  } else {
                    setUninvoicedColumnFilters({
                      reservationNumber: '',
                      date: '',
                      billTo: '',
                      vehicle: '',
                      amount: '',
                      status: '',
                    });
                  }
                  setSortConfig(null);
                  setPage(0);
                }}
                sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
              >{t('common.clearFilters')}</Button>
            </Toolbar>
            <TableContainer>
              <Table 
                size="small" 
                sx={{ 
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { 
                    fontSize: '0.875rem',
                    px: { xs: 2, sm: 3 }
                  }
                }}
              >
            <TableHead>
              <TableRow>
                {viewMode === 'invoices' ? (
                  <>
                    {renderFilterCell(
                      t('fields.invoiceNumber'),
                      invoiceColumnFilters.invoiceNumber,
                      (value) => handleColumnFilterChange('invoiceNumber', value),
                      'invoiceNumber',
                      '120px',
                    )}
                    {renderFilterCell(
                      t('fields.date'),
                      invoiceColumnFilters.date,
                      (value) => handleColumnFilterChange('date', value),
                      'date',
                      '120px',
                    )}
                    {renderFilterCell(
                      t('fields.reservation'),
                      invoiceColumnFilters.reservation,
                      (value) => handleColumnFilterChange('reservation', value),
                      'reservation',
                      '130px',
                    )}
                    {renderFilterCell(
                      t('fields.billTo', 'Bill To'),
                      invoiceColumnFilters.billTo,
                      (value) => handleColumnFilterChange('billTo', value),
                      'billTo',
                      '170px',
                    )}
                    {renderFilterCell(
                      t('fields.amount'),
                      invoiceColumnFilters.amount,
                      (value) => handleColumnFilterChange('amount', value),
                      'amount',
                      '120px',
                    )}
                    {renderFilterCell(
                      t('fields.status'),
                      invoiceColumnFilters.status,
                      (value) => handleColumnFilterChange('status', value),
                      'status',
                      '120px',
                    )}
                    {renderFilterCell(
                      t('fields.dueDate'),
                      invoiceColumnFilters.dueDate,
                      (value) => handleColumnFilterChange('dueDate', value),
                      'dueDate',
                      '120px',
                    )}
                  </>
                ) : (
                  <>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        py: 0.5,
                        px: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        width: '48px'
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allVisibleReservationsSelected}
                        indeterminate={!allVisibleReservationsSelected && someVisibleReservationsSelected}
                        disabled={paginatedReservationIds.length === 0}
                        onChange={handleToggleVisibleReservations}
                      />
                    </TableCell>
                    {renderFilterCell(
                      t('fields.reservationNumber'),
                      uninvoicedColumnFilters.reservationNumber,
                      (value) => handleColumnFilterChange('reservationNumber', value),
                      'reservationNumber',
                      '130px',
                    )}
                    {renderFilterCell(
                      t('fields.date'),
                      uninvoicedColumnFilters.date,
                      (value) => handleColumnFilterChange('date', value),
                      'date',
                      '150px',
                    )}
                    {renderFilterCell(
                      t('fields.billTo', 'Bill To'),
                      uninvoicedColumnFilters.billTo,
                      (value) => handleColumnFilterChange('billTo', value),
                      'billTo',
                      '170px',
                    )}
                    {renderFilterCell(
                      t('fields.vehicle'),
                      uninvoicedColumnFilters.vehicle,
                      (value) => handleColumnFilterChange('vehicle', value),
                      'vehicle',
                      '150px',
                    )}
                    {renderFilterCell(
                      t('fields.amount'),
                      uninvoicedColumnFilters.amount,
                      (value) => handleColumnFilterChange('amount', value),
                      'amount',
                      '120px',
                    )}
                    {renderFilterCell(
                      t('fields.status'),
                      uninvoicedColumnFilters.status,
                      (value) => handleColumnFilterChange('status', value),
                      'status',
                      '120px',
                    )}
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {viewMode === 'invoices' ? (
                loadingInvoices ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      {t('invoices.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((invoice: InvoiceRecord) => {
                    const billTo = getInvoiceBillToInfo(invoice);
                    return (
                    <TableRow
                      key={invoice.id}
                      hover
                      selected={selectedInvoice?.id === invoice.id}
                      onClick={() => handleRowClick(invoice)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {invoice.invoiceNumber}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {formatDate(invoice.invoiceDate)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '130px', width: '130px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {invoice.reservation?.reservationNumber || t('common.unavailable', 'N/A')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '170px', width: '170px' }}>
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {billTo.displayName}
                          </Typography>
                          {billTo.contactPersonName ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {billTo.contactPersonName}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {formatCurrency(invoice.totalAmount)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          <Chip
                            label={statusLabel(t, normalizeInvoiceStatus(invoice.status) ?? invoice.status)}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {formatDate(invoice.dueDate)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )
              ) : (
                loadingReservations ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      {t('invoices.noUninvoiced')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((reservation: Reservation) => {
                    const billTo = getReservationBillToInfo(reservation);
                    const isSelected = selectedReservations.has(reservation.id);
                    const isDifferentBillTo =
                      !isSelected && !!selectedBillToKey && selectedBillToKey !== billTo.key;

                    return (
                    <TableRow
                      key={reservation.id}
                      hover
                      selected={isSelected}
                      sx={{ opacity: isDifferentBillTo ? 0.55 : 1 }}
                    >
                      <TableCell padding="checkbox" sx={{ py: 0.75, px: 1, width: '48px' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleReservation(reservation.id)}
                          disabled={isDifferentBillTo}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '130px', width: '130px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {reservation.reservationNumber}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '150px', width: '150px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {formatDate(reservation.startDate)} - {reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '170px', width: '170px' }}>
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {billTo.displayName}
                          </Typography>
                          {billTo.contactPersonName ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {billTo.contactPersonName}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '150px', width: '150px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {reservation.vehicle
                            ? `${reservation.vehicle.make} ${reservation.vehicle.model}`
                            : t('common.unavailable', 'N/A')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {formatCurrency(reservation.totalAmount)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                        <Box sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          <ReservationStatusChip
                            status={reservation.status}
                            sx={{
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )
              )}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Invoice Details Card */}
        {selectedInvoice && viewMode === 'invoices' && (
          <Grid item xs={12}>
            <InvoiceDetailsPanel invoice={selectedInvoice} showPaymentTotals />
          </Grid>
        )}
      </Grid>

      {selectedInvoice && (
        <EmailSendDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          documentType="invoice"
          documentId={selectedInvoice.id.toString()}
          documentLabel={selectedInvoice.invoiceNumber}
        />
      )}

      {selectedInvoice && (
        <Dialog open={emailLogDialogOpen} onClose={() => setEmailLogDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {t('invoices.sentEmails', 'Sent Emails')}
            <Typography variant="body2" color="text.secondary">
              {selectedInvoice.invoiceNumber}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <EmailLogPanel documentType="invoice" documentId={selectedInvoice.id.toString()} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailLogDialogOpen(false)}>{t('common.close', 'Close')}</Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoices.recordPayment', 'Record Payment')}</DialogTitle>
        <DialogContent>
          {selectedInvoice ? (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t('invoices.paymentDialogSummary', {
                      invoice: selectedInvoice.invoiceNumber,
                      remaining: formatCurrency(selectedInvoiceRemainingBalance),
                      paid: formatCurrency(selectedInvoice.amountPaid || 0),
                      defaultValue: `Invoice ${selectedInvoice.invoiceNumber} has ${formatCurrency(selectedInvoiceRemainingBalance)} remaining and ${formatCurrency(selectedInvoice.amountPaid || 0)} already paid.`,
                    })}
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('fields.amount')}
                    value={paymentForm.amount}
                    onChange={(event) =>
                      setPaymentForm((current) => ({ ...current, amount: event.target.value }))
                    }
                    type="number"
                    inputProps={{ min: 0, max: selectedInvoiceRemainingBalance, step: '0.01' }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DateInputField
                    label={t('fields.paymentDate')}
                    value={paymentForm.paymentDate || todayInput()}
                    onChange={(paymentDate) =>
                      setPaymentForm((current) => ({ ...current, paymentDate }))
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('fields.paymentMethod')}</InputLabel>
                    <Select
                      value={paymentForm.paymentMethod}
                      label={t('fields.paymentMethod')}
                      onChange={(event) =>
                        setPaymentForm((current) => ({
                          ...current,
                          paymentMethod: event.target.value as InvoicePaymentMethod,
                        }))
                      }
                    >
                      <MenuItem value="CASH">{enumLabel(t, 'paymentMethods', 'CASH')}</MenuItem>
                      <MenuItem value="CARD">{enumLabel(t, 'paymentMethods', 'CARD')}</MenuItem>
                      <MenuItem value="BANK_TRANSFER">
                        {enumLabel(t, 'paymentMethods', 'BANK_TRANSFER')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('fields.paymentReference')}
                    value={paymentForm.paymentReference}
                    onChange={(event) =>
                      setPaymentForm((current) => ({
                        ...current,
                        paymentReference: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('fields.notes')}
                    value={paymentForm.notes}
                    onChange={(event) =>
                      setPaymentForm((current) => ({ ...current, notes: event.target.value }))
                    }
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <DialogContentText>
              {t('invoices.validation.selectInvoice')}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            onClick={handleRecordPayment}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            disabled={!selectedInvoice || recordPaymentMutation.isLoading}
          >
            {t('invoices.recordPayment', 'Record Payment')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('invoices.confirmDelete', 'Are you sure you want to delete this invoice?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleConfirmDeleteInvoice} variant="contained" color="error" startIcon={<DeleteIcon />}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      </Container>
    </Box>
  );
};


